import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';
import {
  ArrowRight,
  Crosshair,
  FileOutput,
  ShieldCheck,
  Bluetooth,
  Usb,
  Compass,
  Scan,
  Cable,
  HardHat,
  Building2,
  Landmark,
  MapPinned,
  AlertTriangle,
} from 'lucide-react';

/* ───────────── data ───────────── */

const stats = [
  { value: '0.038 mm', label: 'Resolution' },
  { value: '3', label: 'Kit Options' },
  { value: 'DWG/DXF', label: 'CAD Output' },
  { value: '100%', label: 'Tamper-Proof' },
];

const kits = [
  {
    name: 'Astra Lite Kit',
    sku: 'VR-KIT-000',
    price: '₹3,499',
    description: 'Entry-level kit for basic distance measurement.',
    includes: ['Bridge Lite', 'Road-O-Meter Lite', 'RoM Frame'],
  },
  {
    name: 'Astra Kit',
    sku: 'VR-KIT-001',
    price: '₹5,499',
    description: 'Standard kit with full display and session control.',
    includes: ['Bridge', 'Road-O-Meter Lite', 'RoM Frame'],
    featured: true,
  },
  {
    name: 'Astra Pro Kit',
    sku: 'VR-KIT-002',
    price: '₹10,999',
    description: 'Full-featured kit with sensors, LiDAR, and CAD output.',
    includes: ['Bridge', 'Road-O-Meter', 'RoM Frame'],
  },
];

const comparisonFeatures = [
  { label: 'Bridge', lite: 'Bridge Lite', standard: 'Bridge', pro: 'Bridge' },
  { label: 'Display', lite: '7-Segment (TM1637)', standard: '4" FSTN LCD (ST75256)', pro: '4" FSTN LCD (ST75256)' },
  { label: 'Standalone Control', lite: 'App Required', standard: '✓', pro: '✓' },
  { label: 'Road-O-Meter', lite: 'RoM Lite', standard: 'RoM Lite', pro: 'RoM Standard' },
  { label: '14-Bit Encoder (AS5048A)', lite: '✓', standard: '✓', pro: '✓' },
  { label: 'IMU (LSM6DSV32X)', lite: '—', standard: '—', pro: '✓' },
  { label: 'Compass (QMC5883L)', lite: '—', standard: '—', pro: '✓' },
  { label: 'LiDAR (YDLIDAR T-mini Plus)', lite: '—', standard: '—', pro: '✓' },
  { label: 'Slope Correction', lite: '—', standard: '—', pro: '✓' },
  { label: '2D Path + Dead Reckoning', lite: '—', standard: '—', pro: '✓' },
  { label: 'Cryptographic Signing', lite: '—', standard: '—', pro: '✓' },
  { label: 'CAD Output (DWG/DXF)', lite: '—', standard: '—', pro: '✓' },
  { label: 'WiFi + BLE Export', lite: '✓', standard: '✓', pro: '✓' },
  { label: 'USB-C PD Charging', lite: '✓', standard: '✓', pro: '✓' },
];

const features = [
  {
    icon: Crosshair,
    title: '14-Bit Encoder',
    description: 'AS5048A magnetic encoder with 16,384 counts per revolution — 0.038 mm ground distance resolution.',
  },
  {
    icon: Compass,
    title: 'IMU + Compass',
    description: 'LSM6DSV32X 6-axis IMU and QMC5883L magnetometer for heading, tilt, and slope correction.',
  },
  {
    icon: Scan,
    title: 'LiDAR Integration',
    description: 'YDLIDAR T-mini Plus 2D 360° LiDAR for path-aware spatial mapping.',
  },
  {
    icon: ShieldCheck,
    title: 'Cryptographic Signing',
    description: 'Tamper-evident session logs with cryptographic signatures stored in internal flash.',
  },
  {
    icon: Bluetooth,
    title: 'WiFi + BLE Export',
    description: 'Wireless data export to the Astra Studio mobile app for session management.',
  },
  {
    icon: Usb,
    title: 'USB-C PD Charging',
    description: 'BQ25700A charger with FUSB302 USB-C Power Delivery — 9V/2A fast charging.',
  },
  {
    icon: FileOutput,
    title: 'Direct CAD Output',
    description: 'Generates DWG/DXF files with layers: centreline, walls, dimensions, annotations, and metadata.',
  },
];

const industries = [
  { icon: Cable, title: 'OFC Laying Contractors', description: 'BSNL, Reliance Jio, Airtel vendors — optical fibre cable measurement and documentation.' },
  { icon: HardHat, title: 'CGD Pipeline Contractors', description: 'IGL, MGL, GAIL vendors — city gas distribution pipeline measurement.' },
  { icon: Building2, title: 'Road Construction', description: 'PWD / NHAI road construction measurement teams requiring verified distance data.' },
  { icon: Landmark, title: 'Municipal Corporations', description: 'Underground utility documentation and infrastructure measurement.' },
  { icon: MapPinned, title: 'Survey & Geotechnical Firms', description: 'Lightweight path logging and spatial measurement for field surveys.' },
];

const problems = [
  { title: 'Limited Lifespan', description: 'Mechanical counters fail with wear, requiring frequent replacement.' },
  { title: 'No Path Data', description: 'Only cumulative distance — zero spatial context or path information.' },
  { title: 'No Tamper Resistance', description: 'Measurements can be manually overridden, creating accountability gaps.' },
];

/* ───────────── page ───────────── */

const Astra = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  return (
    <div className="min-h-screen bg-primary">
      <Header />
      <ScrollToTop />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ paddingTop: '80px' }}>
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8c3VydmV5aW5nfGVufDB8fDB8fHww&ixlib=rb-4.1.0&q=85"
            alt="Astra Series"
            className="w-full h-full object-cover opacity-30"
          />
          <div
            className="absolute inset-0"
            style={{ background: isLight ? 'linear-gradient(180deg, rgba(245,245,247,0.75) 0%, rgba(245,245,247,0.97) 100%)' : 'linear-gradient(180deg, rgba(11,14,19,0.7) 0%, rgba(11,14,19,0.95) 100%)' }}
          />
        </div>

        <div className="absolute top-20 right-10 w-32 h-32 rounded-full blur-3xl opacity-20" style={{ background: '#F97316' }} />
        <div className="absolute bottom-20 left-10 w-40 h-40 rounded-full blur-3xl opacity-20" style={{ background: '#A64425' }} />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center space-y-8">
          <span className="text-sm font-bold tracking-widest" style={{ color: '#F97316' }}>
            ASTRA SERIES
          </span>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground-primary">
            Precision Field
            <br />
            <span style={{ color: '#F97316' }}>Measurement, Redefined</span>
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-foreground-secondary">
            Affordable, tamper-proof measurement devices with direct CAD output. Built for civil
            infrastructure contractors who need accuracy they can trust.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Button
              size="lg"
              className="px-8 py-6 text-lg font-semibold transition-smooth group"
              style={{ background: '#F97316', color: '#FFFFFF' }}
              onClick={() => navigate('/contact')}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 0 40px rgba(249,115,22,0.6)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              Get Started
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 pt-12">
            {stats.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                {i > 0 && <span className={`hidden md:block w-px h-10 -ml-4 mr-1 ${isLight ? 'bg-black/10' : 'bg-white/10'}`} />}
                <span className="text-2xl md:text-3xl font-bold" style={{ color: '#F97316' }}>{s.value}</span>
                <span className="text-sm text-foreground-secondary">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#A64425' }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">The Problem</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              Traditional mechanical rodometers have critical flaws that cost contractors time, money, and accountability
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-0">
            {problems.map((p, i) => (
              <div
                key={i}
                className="flex items-start gap-4 py-6 border-b last:border-b-0"
                className="border-subtle"
              >
                <span className="text-2xl font-bold" style={{ color: '#F97316' }}>{i + 1}</span>
                <div>
                  <h3 className="text-lg font-semibold text-foreground-primary mb-1">{p.title}</h3>
                  <p className="text-sm leading-relaxed text-foreground-secondary">{p.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kits / Pricing */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#F97316' }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">Choose Your Kit</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              Three options designed for different needs and budgets
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {kits.map((kit, i) => (
              <div
                key={i}
                className={`glass-frosted rounded-3xl p-8 transition-smooth glow-subtle relative ${
                  kit.featured ? 'ring-2 ring-orange-500' : ''
                }`}
              >
                {kit.featured && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold"
                    style={{ background: '#F97316', color: '#FFFFFF' }}
                  >
                    POPULAR
                  </div>
                )}
                <div className="text-center space-y-4">
                  <p className="text-xs text-foreground-secondary tracking-wide">{kit.sku}</p>
                  <h3 className="text-2xl font-bold text-foreground-primary">{kit.name}</h3>
                  <div className="text-4xl font-bold" style={{ color: '#F97316' }}>{kit.price}</div>
                  <p className="text-sm text-foreground-secondary">{kit.description}</p>

                  <div className="pt-4 space-y-2">
                    <p className="text-xs font-semibold text-foreground-primary uppercase tracking-wide">Includes</p>
                    {kit.includes.map((item, j) => (
                      <div key={j} className="flex items-center justify-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                        <span className="text-sm text-foreground-secondary">{item}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    className="w-full mt-4 font-semibold transition-smooth"
                    style={{ background: kit.featured ? '#F97316' : 'transparent', color: kit.featured ? '#FFFFFF' : undefined, border: kit.featured ? 'none' : isLight ? '1px solid rgba(0,0,0,0.15)' : '1px solid rgba(255,255,255,0.2)' }}
                    className={kit.featured ? '' : 'text-foreground-primary'}
                    onClick={() => navigate('/contact')}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    Inquire Now
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Comparison Table */}
          <div className="glass-frosted rounded-3xl p-6 md:p-10 glow-subtle overflow-x-auto">
            <h3 className="text-xl font-bold text-foreground-primary mb-6 text-center">Feature Comparison</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" className="border-subtle">
                  <th className="text-left py-3 pr-4 text-foreground-secondary font-medium">Feature</th>
                  <th className="py-3 px-4 text-center text-foreground-secondary font-medium">Lite</th>
                  <th className="py-3 px-4 text-center font-medium" style={{ color: '#F97316' }}>Standard</th>
                  <th className="py-3 px-4 text-center text-foreground-secondary font-medium">Pro</th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((row, i) => (
                  <tr key={i} className="border-b last:border-b-0" className="border-subtle">
                    <td className="py-3 pr-4 text-foreground-primary whitespace-nowrap">{row.label}</td>
                    <td className="py-3 px-4 text-center text-foreground-secondary">{row.lite}</td>
                    <td className="py-3 px-4 text-center text-foreground-primary font-medium">{row.standard}</td>
                    <td className="py-3 px-4 text-center text-foreground-secondary">{row.pro}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Features / Technology */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#35474B' }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">Technology Inside</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              Engineering precision from the ground up
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-0 max-w-5xl mx-auto">
            {features.map((f, i) => (
              <div
                key={i}
                className="flex items-start gap-4 py-5 border-b last:border-b-0"
                className="border-subtle"
              >
                <f.icon size={20} style={{ color: '#F97316', marginTop: '3px' }} className="flex-shrink-0" />
                <div>
                  <h3 className="text-base font-semibold text-foreground-primary mb-1">{f.title}</h3>
                  <p className="text-sm leading-relaxed text-foreground-secondary">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Industries */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#F97316' }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">Built For</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              Serving the contractors and teams who build India's infrastructure
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-0">
            {industries.map((ind, i) => (
              <div
                key={i}
                className="flex items-start gap-4 py-5 border-b last:border-b-0"
                className="border-subtle"
              >
                <ind.icon size={20} style={{ color: '#F97316', marginTop: '3px' }} className="flex-shrink-0" />
                <div>
                  <h3 className="text-base font-semibold text-foreground-primary mb-1">{ind.title}</h3>
                  <p className="text-sm leading-relaxed text-foreground-secondary">{ind.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">
            Get Started with Astra
          </h2>
          <p className="text-lg text-foreground-secondary max-w-2xl mx-auto">
            Replace outdated measurement tools with precision, accountability, and direct CAD output.
          </p>
          <Button
            size="lg"
            className="px-8 py-6 text-lg font-semibold transition-smooth group"
            style={{ background: '#F97316', color: '#FFFFFF' }}
            onClick={() => navigate('/contact')}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 0 40px rgba(249,115,22,0.6)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            Contact Us
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Astra;

/* ───────────── sub-components ───────────── */

/* FeatureCard removed — features now rendered inline */
